/**
 * TodaySchedule Component
 * Displays today's appointments list with time, employee name, and exam type
 *
 * @version 2025.1.0
 */
import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  Chip,
  Button,
  Skeleton,
  Divider,
} from '@mui/material';
import {
  Schedule,
  ArrowForward,
  EventBusy,
} from '@mui/icons-material';
import { TodayScheduleProps, TodayAppointment } from '../../../types/dashboard';

/**
 * Get status chip color based on appointment status
 */
const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'default' | 'info' => {
  switch (status.toUpperCase()) {
    case 'CONFIRMADO':
    case 'CONFIRMED':
      return 'success';
    case 'PENDENTE':
    case 'PENDING':
      return 'warning';
    case 'CANCELADO':
    case 'CANCELLED':
      return 'error';
    case 'REALIZADO':
    case 'COMPLETED':
      return 'info';
    default:
      return 'default';
  }
};

/**
 * Appointment list item component
 */
const AppointmentItem: React.FC<{ appointment: TodayAppointment; isLast: boolean }> = ({
  appointment,
  isLast,
}) => {
  return (
    <>
      <ListItem
        sx={{
          px: 0,
          py: 1.5,
        }}
      >
        <Box
          sx={{
            minWidth: 60,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            color: 'text.secondary',
          }}
        >
          <Schedule sx={{ fontSize: 16 }} />
          <Typography variant="body2" fontWeight={600}>
            {appointment.time}
          </Typography>
        </Box>
        <ListItemText
          primary={
            <Typography variant="body2" fontWeight={500}>
              {appointment.employeeName}
            </Typography>
          }
          secondary={
            <Typography variant="caption" color="text.secondary">
              {appointment.examTypeLabel || appointment.examType} - {appointment.professionalName}
            </Typography>
          }
          sx={{ ml: 2 }}
        />
        <Chip
          label={appointment.statusLabel || appointment.status}
          size="small"
          color={getStatusColor(appointment.status)}
          sx={{ ml: 1 }}
        />
      </ListItem>
      {!isLast && <Divider />}
    </>
  );
};

/**
 * Loading skeleton for appointments
 */
const LoadingSkeleton: React.FC = () => (
  <Box>
    {[1, 2, 3].map((i) => (
      <Box key={i} sx={{ display: 'flex', alignItems: 'center', py: 1.5 }}>
        <Skeleton variant="text" width={50} height={24} />
        <Box sx={{ ml: 2, flex: 1 }}>
          <Skeleton variant="text" width="60%" height={20} />
          <Skeleton variant="text" width="40%" height={16} />
        </Box>
        <Skeleton variant="rounded" width={80} height={24} />
      </Box>
    ))}
  </Box>
);

/**
 * Empty state when no appointments
 */
const EmptyState: React.FC = () => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      py: 4,
      color: 'text.secondary',
    }}
  >
    <EventBusy sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
    <Typography variant="body2">Nenhum agendamento para hoje</Typography>
  </Box>
);

const TodaySchedule: React.FC<TodayScheduleProps> = ({
  appointments,
  loading = false,
  onViewAll,
}) => {
  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Schedule color="primary" />
            Agenda de Hoje
          </Typography>
          {appointments.length > 0 && (
            <Chip
              label={`${appointments.length} agendamento${appointments.length > 1 ? 's' : ''}`}
              size="small"
              color="primary"
              variant="outlined"
            />
          )}
        </Box>

        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {loading ? (
            <LoadingSkeleton />
          ) : appointments.length === 0 ? (
            <EmptyState />
          ) : (
            <List disablePadding>
              {appointments.map((appointment, index) => (
                <AppointmentItem
                  key={appointment.id}
                  appointment={appointment}
                  isLast={index === appointments.length - 1}
                />
              ))}
            </List>
          )}
        </Box>

        {onViewAll && appointments.length > 0 && (
          <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
            <Button
              variant="text"
              color="primary"
              endIcon={<ArrowForward />}
              onClick={onViewAll}
              fullWidth
              sx={{ justifyContent: 'center' }}
            >
              Ver agenda completa
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default TodaySchedule;
